import * as React from 'react';
import { BodyLarge, BodyRegular, Headline3, NavigationLink } from '../Elements';
import styled from 'styled-components';
import { breakpoints, FONTSIZE, sizes, theme } from '../../theme';
import ValidateIcon from '../../assets/icons/indicators/ValidateIcon';
import { IconSize } from '../../assets/icons/icon-sizes';
import InformationIcon from '../../assets/icons/indicators/InformationIcon';
import WarningIcon from '../../assets/icons/indicators/WarningIcon';
import ErrorIcon from '../../assets/icons/indicators/ErrorIcon';
import strings from '../../strings/strings.json';
import Button from 'components/buttons/Button';

export type LinkToHelp = {
    text: string;
    aria: string;
    url: string;
};

export type ButtonToHelp = {
    text: string;
    aria: string;
    action: () => void;
    disabled?: boolean;
    icon?: JSX.Element;
};

export enum NotificationType {
    WARNING = 'WARNING',
    DANGER = 'DANGER',
    SUCCESS = 'SUCCESS',
    INFO = 'INFO',
}

type NotificationProps = {
    notificationType: NotificationType;
    title?: string;
    description?: string;
    big?: boolean;
    linkToHelp?: LinkToHelp;
    buttonToHelp?: ButtonToHelp;
    maxWidth?: string;
    buttonOnSameRow?: boolean;
    button?: JSX.Element;
    dataTestId?: string;
};

export default function Notification({
    notificationType,
    title,
    description,
    big = false,
    linkToHelp,
    buttonToHelp,
    maxWidth,
    buttonOnSameRow,
    button,
    dataTestId,
}: NotificationProps): JSX.Element {
    const notificationTitle = title ?? strings.general.saveSuccess;

    return (
        <NotificationContainer
            $notificationType={notificationType}
            $big={big}
            $maxWidth={maxWidth}
            data-testId={dataTestId}
        >
            {big ? (
                <>
                    <div>{getIcon(notificationType, big)}</div>
                    <div>
                        <Headline3>{notificationTitle}</Headline3>
                        {description && <BigDescriptionSection>{description}</BigDescriptionSection>}
                    </div>
                </>
            ) : (
                <NotificationBodyContainer>
                    <TitleAndIconRow>
                        <div>{getIcon(notificationType, big)}</div>
                        <BodyLarge $bold>{notificationTitle}</BodyLarge>
                        {buttonToHelp && buttonOnSameRow && (
                            <ButtonToHelpRowContainer>
                                <Button
                                    title={buttonToHelp.text}
                                    aria={buttonToHelp.aria}
                                    onClick={buttonToHelp.action}
                                    icon={buttonToHelp.icon}
                                    disabled={buttonToHelp.disabled}
                                />
                            </ButtonToHelpRowContainer>
                        )}
                    </TitleAndIconRow>

                    {description && <DescriptionSection>{description}</DescriptionSection>}
                    {buttonToHelp && !buttonOnSameRow && (
                        <ButtonToHelpContainer>
                            <Button
                                title={buttonToHelp.text}
                                aria={buttonToHelp.aria}
                                onClick={buttonToHelp.action}
                                icon={buttonToHelp.icon}
                                disabled={buttonToHelp.disabled}
                            />
                        </ButtonToHelpContainer>
                    )}
                    {button && <div>{button}</div>}
                    {linkToHelp && (
                        <p>
                            <HelpNavigationLink to={linkToHelp.url} aria-label={linkToHelp.aria} target="_blank">
                                {linkToHelp.text}
                            </HelpNavigationLink>
                        </p>
                    )}
                </NotificationBodyContainer>
            )}
        </NotificationContainer>
    );
}

function getIcon(notificationType: NotificationType, big: boolean = false): JSX.Element {
    if (big) {
        if (notificationType === NotificationType.DANGER) {
            return (
                <BigIconContainer>
                    <ErrorIcon size={IconSize.LARGE} />
                </BigIconContainer>
            );
        }
        if (notificationType === NotificationType.SUCCESS) {
            return (
                <BigIconContainer>
                    <ValidateIcon size={IconSize.LARGE} />
                </BigIconContainer>
            );
        }
        if (notificationType === NotificationType.WARNING) {
            return (
                <BigIconContainer>
                    <WarningIcon size={IconSize.LARGE} />
                </BigIconContainer>
            );
        }
        return (
            <BigIconContainer>
                <InformationIcon size={IconSize.LARGE} colour={theme.textColour} />
            </BigIconContainer>
        );
    } else {
        if (notificationType === NotificationType.DANGER)
            return (
                <SmallIconContainer>
                    <ErrorIcon />
                </SmallIconContainer>
            );
        if (notificationType === NotificationType.SUCCESS) {
            return (
                <SmallIconContainer>
                    <ValidateIcon />
                </SmallIconContainer>
            );
        }
        if (notificationType === NotificationType.WARNING) {
            return (
                <SmallIconContainer>
                    <WarningIcon />
                </SmallIconContainer>
            );
        }
        return (
            <SmallIconContainer>
                <InformationIcon colour={theme.textColour} />
            </SmallIconContainer>
        );
    }
}

const TitleAndIconRow = styled.div`
    display: flex;
    gap: ${sizes.spacingSm};
    align-items: center;
`;

const ButtonToHelpContainer = styled.div`
    width: 100px;
    @media (max-width: ${breakpoints.sm}) {
        width: 100%;
    }
`;

const ButtonToHelpRowContainer = styled.div`
    padding-left: ${sizes.spacingSm};
`;

const HelpNavigationLink = styled(NavigationLink)`
    padding: unset;
`;

const NotificationBodyContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${sizes.spacingSm};
`;

const BigIconContainer = styled.div`
    margin-top: 0.8rem;
`;

const SmallIconContainer = styled.div`
    margin-top: 0.3rem;
`;

const BigDescriptionSection = styled.p`
    padding-top: ${sizes.spacingMd};
    color: ${theme.textSecondaryColour};
    font-size: ${FONTSIZE.Headline5};
    font-weight: 600;
    white-space: pre-wrap;
`;

const DescriptionSection = styled(BodyRegular)`
    white-space: pre-wrap;
`;

const NotificationContainer = styled.div<{
    $notificationType?: NotificationType;
    $big?: boolean;
    $maxWidth?: string;
}>`
    @media (max-width: ${breakpoints.sm}) {
        width: 100%;
    }
    display: flex;
    border-radius: ${sizes.borderRadiusMd};
    padding: ${({ $big }) => {
        return $big ? sizes.spacingLg : sizes.spacingMd;
    }};
    gap: ${sizes.spacingMd};
    border-width: 3px;
    border-style: solid;
    max-width: ${({ $maxWidth }) => {
        return $maxWidth ? $maxWidth : 'fit-content';
    }};
    border-color: ${({ $notificationType }) => {
        if ($notificationType === NotificationType.SUCCESS) return theme.notificationSuccessBorder;
        if ($notificationType === NotificationType.DANGER) return theme.notificationDangerBorder;
        if ($notificationType === NotificationType.WARNING) return theme.notificationWarningBorder;
        return theme.notificationInfoBorder;
    }};
    background-color: ${({ $notificationType }) => {
        if ($notificationType === NotificationType.SUCCESS) return theme.notificationSuccessBackground;
        if ($notificationType === NotificationType.DANGER) return theme.notificationDangerBackground;
        if ($notificationType === NotificationType.WARNING) return theme.notificationWarningBackground;
        return theme.notificationInfoBackground;
    }};
`;
